import { useUnit } from 'effector-react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { Button } from '@/components/ui/button'
import { Dialog } from '@/components/ui/dialog'
import { TextField } from '@/components/ui/text-field'
import { $activeChatDetails } from '@/models/chattingUI/chats/chats.ts'
import { useCountdowns } from '@/modules/chatting/state/countdown-provider.tsx'

import { DialogProps } from '../types'
import { CountdownMinutesSlider } from './CountdownMinutesSlider'
import {
  COUNTDOWN_FORM_ID,
  CountdownFormValues,
  MAX_COUNTDOWN_MINUTES,
  MIN_COUNTDOWN_MINUTES,
  countdownFormResolver,
} from './validation'

export const CountdownDialog = ({ isOpen, onClose }: DialogProps) => {
  const activeChat = useUnit($activeChatDetails)
  const { addCountdown } = useCountdowns()
  const form = useForm<CountdownFormValues>({
    resolver: countdownFormResolver,
    mode: 'onChange',
  })

  form.watch('minutes')

  const onSubmit = (values: CountdownFormValues) => {
    const { id, displayName, name, username } = activeChat?.withUser ?? {}
    const chatName = displayName || name || username

    if (!id) return

    addCountdown(id, chatName ?? '', values.minutes)
    onClose()
  }

  return (
    <Dialog
      show={isOpen}
      onClose={onClose}
      panelClassName="lg:max-w-[533px]"
      bigDialog
    >
      <div className="space-y-6">
        <Dialog.Header>
          <Dialog.Title>Start Countdown</Dialog.Title>
        </Dialog.Header>
        <FormProvider {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            id={COUNTDOWN_FORM_ID}
          >
            <Controller
              control={form.control}
              name="minutes"
              render={({ field: { onChange, value } }) => (
                <div className="space-y-6">
                  <div className="space-y-1.5">
                    <label className="text-sm font-medium text-title">
                      Minutes
                    </label>
                    <TextField
                      value={value}
                      placeholder={`${MIN_COUNTDOWN_MINUTES} - ${MAX_COUNTDOWN_MINUTES} Minutes`}
                      max={MAX_COUNTDOWN_MINUTES}
                      onChange={(event) => {
                        const v = Number(event.target.value)

                        if (v > MAX_COUNTDOWN_MINUTES) return

                        onChange(isNaN(v) ? MIN_COUNTDOWN_MINUTES : v)
                      }}
                    />
                  </div>
                  <CountdownMinutesSlider
                    onValueChange={([value]) => onChange(value)}
                    value={[value || MIN_COUNTDOWN_MINUTES]}
                    max={MAX_COUNTDOWN_MINUTES}
                    step={1}
                  />
                </div>
              )}
            />
          </form>
        </FormProvider>
      </div>
      <Dialog.ClosableFooter onClose={onClose}>
        <Button
          form={COUNTDOWN_FORM_ID}
          disabled={!form.formState.isDirty || !form.formState.isValid}
        >
          Start
        </Button>
      </Dialog.ClosableFooter>
    </Dialog>
  )
}
